import React, { PropsWithChildren } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { BsViewList } from '@react-icons/all-files/bs/BsViewList';
import { CarouselControlButton } from '@bucketplace/design-system/bds';
import { Carousel as CarouselUI } from '@bucketplace/ui';

import { EditorPlaceholder } from '../../components/EditorPlaceholder';
import { useEditorNode } from '../../hooks/useEditorNode';
import { HOVER } from '../../styles';
import { DocumentPath } from '../../types';
import { CarouselPagination } from './CarouselPagination';

export interface CarouselProps {
  autoPlay: boolean;
  autoPlayInterval?: number;
  loop: boolean;
  childWidthPercentage: number;
  align: 'left' | 'center' | 'centerConstrained';
  hasArrow: boolean;
  hasPagination: boolean;
  path: DocumentPath;
}

export function Carousel({
  children,
  loop,
  childWidthPercentage,
  hasArrow,
  hasPagination,
  path,
  align,
  ...props
}: PropsWithChildren<CarouselProps>) {
  const { displayMode } = useEditorNode(path);

  return (
    <CarouselUI
      disableWrap={!loop}
      childWidthRatio={childWidthPercentage > 0 ? childWidthPercentage / 100 : undefined}
      {...props}
      align={align === 'left' ? align : 'center'}
      renderControls={(props) => {
        const { offset, max, min, prev, next } = props;
        const length = max + 1;
        const size = displayMode === 'edit' ? length + 1 : length;

        return (
          <>
            { hasPagination && (
              <CarouselPagination
                size={Math.max(size, 0)}
                currentIndex={offset}
              />
            ) }
            { hasArrow && (
              <>
                <LeftControlButton
                  iconName="chevron_left"
                  position="left"
                  onClick={() => prev()}
                  aria-label="이전"
                  hidden={(!loop && offset <= min)}
                  aria-hidden={!loop && offset <= min}
                />
                <RightControlButton
                  iconName="chevron_right"
                  position="right"
                  onClick={() => next()}
                  aria-label="다음"
                  hidden={(!loop && offset >= max)}
                  aria-hidden={!loop && offset >= max}
                />
              </>
            ) }
          </>
        );
      }}
    >
      { childWidthPercentage > 0 && (children || (
        <EditorPlaceholder icon={<Icon />} message="아이템을 추가해주세요." />
      )) }
    </CarouselUI>
  );
}

const Icon = styled(BsViewList)`
  transform: rotate(90deg);
`;

const LeftControlButton = styled(CarouselControlButton)`
  transform: translate(0, -50%);
  left: 10px;
  right: initial;
  > button {
    ${({ theme }) => css`
      &:hover, &:active {
        background-color: ${theme.content.base6};
      }
      ${HOVER`
        background-color: ${theme.interactive.transparent};
      `}
    `}
  }
`;

const RightControlButton = styled(CarouselControlButton)`
  transform: translate(0, -50%);
  left: initial;
  right: 10px;
  > button {
    ${({ theme }) => css`
      &:hover, &:active {
        background-color: ${theme.content.base6};
      }
      ${HOVER`
        background-color: ${theme.interactive.transparent};
      `}
    `}
  }
`;

